import {useGetPlayerAppointments} from '@/api/MLSChallenge/useGetPlayerAppointments';
import {useProfiles} from '@/api/User/useProfiles';
import Loader from '@/components/MLS/Loader/Loader';
import {NewPageHeading} from '@/components/MLS/NewPageHeading/NewPageHeading';
import {useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {getDisplayData} from './utils';

export const renderLevel = (level: string) => {
  if (typeof level === 'string') {
    const parsed = Number(level);
    if (!isNaN(parsed)) {
      return `${parsed} / 10`;
    }
    return level;
  }
  return level;
};

export const MyScore = ({isTrainer = false}: {isTrainer?: boolean}) => {
  const navigate = useNavigate();
  const {currentProfile} = useProfiles();
  const [searchParams] = useSearchParams();
  const {data, isLoading} = useGetPlayerAppointments({
    playerProfileId: isTrainer ? searchParams.get('athleteId') || '' : currentProfile?._id,
    includeFutureAppointments: false,
    sessionSummariesLimit: 5,
    filterScoredSessions: true,
  });

  const sessionSummariesWithScore = useMemo(
    () => data?.sessionSummaries?.summaries?.filter((summary) => summary.summary.session.tocaScore) || [],
    [data]
  );

  const averageScore = isTrainer ? Number(searchParams.get('score')) : currentProfile?.score?.averageScore;
  const lastSession = useMemo(() => data?.sessionSummaries?.summaries?.[0], [data]);
  const displayData = useMemo(() => getDisplayData(sessionSummariesWithScore), [sessionSummariesWithScore]);

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <Loader text="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-start bg-white py-8 px-2 sm:px-0">
      {/* Header */}
      <NewPageHeading text="Toca Score" onClickOverride={() => navigate(-1)} />
      <div className="flex flex-col gap-8 w-full max-w-2xl">
        {/* Score Circle */}
        <div className="flex items-center justify-center">
          <div className="h-[200px] w-[200px] sm:h-[300px] sm:w-[300px] flex items-center justify-center rounded-full bg-mls-sky-blue shadow-lg">
            <span className="font-teko text-[100px] sm:text-[160px]">{Math.round(averageScore ?? 0)}</span>
          </div>
        </div>
        {/* Player Level */}
        {lastSession?.level && (
          <div className="text-center">
            <span className="text-lg sm:text-xl font-semibold capitalize">
              Level: {renderLevel(currentProfile?.currentLevel || lastSession.level)}
            </span>
          </div>
        )}

        {/* Last 5 sessions */}
        <div className="flex flex-col gap-4">
          <div className="flex gap-1 xs:gap-4 sm:gap-6 md:gap-12 justify-center mt-2 xs:px-2">
            {displayData.map((item, idx) => (
              <div key={item.sessionId} className="flex flex-col items-center">
                <div
                  className={`h-12 w-12 xs:h-12 xs:w-12 sm:h-16 sm:w-16 md:h-24 md:w-24 flex items-center justify-center rounded-full text-sm xs:text-lg sm:text-2xl md:text-3xl font-bold shadow-md transition-all duration-200
                    ${item.isBaselineSession ? 'bg-mls-baseline text-white' : 'bg-mls-sky-blue text-mls-secondary'}`}
                >
                  {item.tocaScore}
                </div>
                <span className="text-xs sm:text-sm md:text-base text-gray-500 mt-2">
                  {item.isBaselineSession ? 'Baseline' : item.date}
                </span>
              </div>
            ))}
          </div>
          {displayData.length > 0 ? (
            <div className="text-center text-base sm:text-xl mt-4">Last {displayData.length} sessions</div>
          ) : (
            <div className="text-center text-base sm:text-xl mt-4">No sessions found</div>
          )}
        </div>
      </div>
    </div>
  );
};
